<script lang="ts" module>
  import type { Component } from 'svelte';
  export interface MenuItem {
    label?: string;
    icon?: Component<any>;
    onclick?: () => void;
    href?: string;
    danger?: boolean;
    disabled?: boolean;
    divider?: boolean;
    hint?: string;
    checked?: boolean;
    heading?: boolean;
  }
</script>

<script lang="ts">
  import type { Snippet } from 'svelte';
  import { Check } from '@lucide/svelte';
  import Popover from './Popover.svelte';
  import { navigate } from '../router.svelte';

  interface Props {
    items: MenuItem[];
    placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end' | 'right-start';
    width?: string;
    trigger: Snippet<[{ toggle: () => void; open: boolean }]>;
  }
  let { items, placement = 'bottom-end', width, trigger }: Props = $props();
  let open = $state(false);
  let anchor: HTMLElement | null = $state(null);
  let list: HTMLElement | null = $state(null);

  function choose(item: MenuItem) {
    if (item.disabled) return;
    open = false;
    if (item.href) navigate(item.href);
    item.onclick?.();
  }
  function keynav(event: KeyboardEvent) {
    if (!list) return;
    const buttons = Array.from(list.querySelectorAll<HTMLButtonElement>('button:not([disabled])'));
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      buttons[(index + 1) % buttons.length]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      buttons[(index - 1 + buttons.length) % buttons.length]?.focus();
    }
  }
  $effect(() => {
    if (open && list) queueMicrotask(() => list?.querySelector<HTMLButtonElement>('button:not([disabled])')?.focus());
  });
</script>

<span class="anchor" bind:this={anchor}>
  {@render trigger({ toggle: () => (open = !open), open })}
</span>
<Popover bind:open {anchor} {placement} {width}>
  <div class="menu" role="menu" tabindex="-1" bind:this={list} onkeydown={keynav}>
    {#each items as item, i (i)}
      {#if item.divider}
        <div class="divider" role="separator"></div>
      {:else if item.heading}
        <div class="heading">{item.label}</div>
      {:else}
        <button type="button" role={item.checked !== undefined ? 'menuitemcheckbox' : 'menuitem'} aria-checked={item.checked} class="item" class:danger={item.danger} disabled={item.disabled} onclick={() => choose(item)}>
          {#if item.checked !== undefined}
            <span class="check">{#if item.checked}<Check size={15} />{/if}</span>
          {:else if item.icon}
            <item.icon size={16} />
          {/if}
          <span class="lbl">{item.label}</span>
          {#if item.hint}<span class="hint">{item.hint}</span>{/if}
        </button>
      {/if}
    {/each}
  </div>
</Popover>

<style>
  .anchor { display: inline-flex; }
  .menu { padding: var(--space-1); display: flex; flex-direction: column; min-width: 200px; outline: none; }
  .item {
    display: flex; align-items: center; gap: var(--space-2_5);
    width: 100%; height: 36px; padding: 0 var(--space-2_5);
    border: 0; border-radius: var(--radius-xs);
    background: transparent; color: var(--text);
    font-size: var(--text-base); text-align: left;
  }
  .item :global(svg) { color: var(--text-2); flex-shrink: 0; }
  .item:hover, .item:focus-visible { background: var(--surface-hover); outline: none; }
  .item.danger { color: var(--danger); }
  .item.danger :global(svg) { color: var(--danger); }
  .item.danger:hover, .item.danger:focus-visible { background: var(--danger-soft); }
  .item:disabled { opacity: 0.45; }
  .lbl { flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .hint { font-size: var(--text-xs); color: var(--text-3); }
  .check { width: 16px; display: inline-flex; color: var(--brand); }
  .divider { height: 1px; background: var(--border); margin: var(--space-1) var(--space-1); }
  .heading { font-size: var(--text-2xs); text-transform: uppercase; letter-spacing: var(--tracking-caps); font-weight: var(--weight-semibold); color: var(--text-3); padding: var(--space-2) var(--space-2_5) var(--space-1); }
</style>
